import { useState, useEffect, useRef } from 'react';
import { useSearchParams, useParams } from 'react-router-dom';
import {
  CheckCircle2,
  Clock,
  AlertCircle,
  ShieldCheck,
  Layers,
  Copy,
  Check,
  FileCheck,
  PenTool,
  Printer,
  X,
  Award,
  Hash,
  FileText,
  DollarSign,
  FolderKanban,
  ExternalLink,
  MessageCircle,
  Eye,
  EyeOff,
  CreditCard,
  Send,
  LogOut,
  ChevronRight,
  FileBox,
  Search,
  History,
} from 'lucide-react';
import { api } from '@/lib/api.js';
import { Button } from '@/components/ui/button.js';
import { Input } from '@/components/ui/input.js';
import { Badge } from '@/components/ui/badge.js';
import { Card, CardContent } from '@/components/ui/card.js';
import { ClientPortalDashboardData } from '@/types/index.js';

interface MilestoneDeliverable {
  id: string;
  project_id?: string;
  project_name?: string;
  title: string;
  description?: string;
  status: 'pending' | 'in_progress' | 'completed' | 'delivered' | string;
  due_date?: string | null;
  order_index?: number;
  approved_at?: string | null;
  approved_by_name?: string | null;
  approved_by_title?: string | null;
  approved_signature_data?: string | null;
  certificate_id?: string | null;
  certificate_hash?: string | null;
  signer_ip?: string | null;
  is_approved?: boolean;
}

interface HandoverCertificate {
  certificate_id: string;
  certificate_hash: string;
  project_id: string;
  project_name: string;
  client_name: string;
  milestone_id: string;
  milestone_title: string;
  milestone_order: number;
  completed_at: string;
  approved_at: string;
  signer_name: string;
  signer_title: string;
  signer_ip: string;
  signature_data: string;
  verification_status: string;
  issued_by: string;
}

function ProgressRing({ progress, size = 100, strokeWidth = 8 }: { progress: number; size?: number; strokeWidth?: number }) {
  const radius = (size - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;
  const clampedProgress = Math.min(100, Math.max(0, progress));
  const strokeDashoffset = circumference - (clampedProgress / 100) * circumference;

  return (
    <div className="relative flex items-center justify-center shrink-0" style={{ width: size, height: size }}>
      <svg width={size} height={size} className="transform -rotate-90">
        <defs>
          <linearGradient id="portalRingGradient" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="#14b8a6" />
            <stop offset="50%" stopColor="#10b981" />
            <stop offset="100%" stopColor="#34d399" />
          </linearGradient>
        </defs>
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          stroke="rgba(20, 184, 166, 0.15)"
          strokeWidth={strokeWidth}
          fill="transparent"
        />
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          stroke="url(#portalRingGradient)"
          strokeWidth={strokeWidth}
          strokeDasharray={circumference}
          strokeDashoffset={strokeDashoffset}
          strokeLinecap="round"
          fill="transparent"
          className="transition-all duration-1000 ease-out"
        />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center text-center">
        <span className="text-xl font-black text-white font-mono leading-none">
          {clampedProgress}%
        </span>
        <span className="text-[9px] uppercase font-bold tracking-wider text-teal-400 mt-0.5">
          Progress
        </span>
      </div>
    </div>
  );
}

export function ClientPortalPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const routeParams = useParams<{ certificateId?: string; token?: string }>();
  const token = searchParams.get('token') || routeParams.token;
  const certificateIdFromRoute = routeParams.certificateId;

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [dashboardData, setDashboardData] = useState<ClientPortalDashboardData | null>(null);
  const [activeTab, setActiveTab] = useState<'overview' | 'tasks' | 'milestones' | 'proposals' | 'deliverables' | 'invoices'>('overview');
  const [selectedProjectId, setSelectedProjectId] = useState<string>('all');
  const [projectScopeView, setProjectScopeView] = useState<'active' | 'completed' | 'all'>('active');
  const [fileFilter, setFileFilter] = useState<'all' | 'design' | 'technical' | 'legal'>('all');
  const [taskStatusFilter, setTaskStatusFilter] = useState<string>('all');
  const [taskMilestoneFilter, setTaskMilestoneFilter] = useState<string>('all');
  const [taskSearchQuery, setTaskSearchQuery] = useState('');

  const [copiedLink, setCopiedLink] = useState(false);
  const [copiedCertId, setCopiedCertId] = useState(false);

  // Unauthenticated Portal Auth Form State
  const [authMode, setAuthMode] = useState<'password' | 'magic' | 'token'>('password');
  const [loginEmail, setLoginEmail] = useState('');
  const [loginPassword, setLoginPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [magicEmail, setMagicEmail] = useState('');
  const [directTokenInput, setDirectTokenInput] = useState('');
  const [authSubmitting, setAuthSubmitting] = useState(false);
  const [authError, setAuthError] = useState<string | null>(null);
  const [authSuccessMsg, setAuthSuccessMsg] = useState<string | null>(null);

  // E-Signature & Milestone Approval State
  const [activeSignMilestone, setActiveSignMilestone] = useState<MilestoneDeliverable | null>(null);
  const [signMode, setSignMode] = useState<'draw' | 'type'>('draw');
  const [signerName, setSignerName] = useState('');
  const [signerTitle, setSignerTitle] = useState('Authorized Client Representative');
  const [declarationAgreed, setDeclarationAgreed] = useState(false);
  const [isSubmittingSignature, setIsSubmittingSignature] = useState(false);
  const [signError, setSignError] = useState<string | null>(null);

  // Canvas Drawing State
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const lastPointRef = useRef<{ x: number; y: number } | null>(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [hasDrawn, setHasDrawn] = useState(false);

  // Certificate Modal State
  const [viewingCertificate, setViewingCertificate] = useState<HandoverCertificate | null>(null);
  const [standaloneCert, setStandaloneCert] = useState<HandoverCertificate | null>(null);

  // Load Standalone Certificate
  useEffect(() => {
    if (certificateIdFromRoute) {
      let isMounted = true;
      async function loadStandaloneCert() {
        setLoading(true);
        setError(null);
        try {
          const res = await api.get<any>(`/api/v1/portal/certificates/${certificateIdFromRoute}`);
          if (isMounted) {
            setStandaloneCert(res.data || res);
          }
        } catch (err: any) {
          if (isMounted) {
            setError(err.message || 'Milestone completion certificate not found or invalid.');
          }
        } finally {
          if (isMounted) {
            setLoading(false);
          }
        }
      }
      loadStandaloneCert();
      return () => {
        isMounted = false;
      };
    }
  }, [certificateIdFromRoute]);

  // Load Dashboard Data
  useEffect(() => {
    if (certificateIdFromRoute) return;

    let isMounted = true;
    async function fetchDashboard() {
      setLoading(true);
      setError(null);
      try {
        const url = token
          ? `/api/v1/client-portal/dashboard?token=${encodeURIComponent(token)}`
          : `/api/v1/client-portal/dashboard`;
        const res = await api.get<any>(url);
        if (isMounted) {
          const data: ClientPortalDashboardData = res.data || res;
          setDashboardData(data);
          if (data.client?.name) {
            setSignerName(data.client.name);
          }
          if (!token && data.client?.portal_token) {
            setSearchParams({ token: data.client.portal_token }, { replace: true });
          }
        }
      } catch (err: any) {
        if (isMounted) {
          if (token) {
            setError(err.message || 'Invalid or expired portal token. Please log in or request a new magic link.');
          }
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    }

    fetchDashboard();
    return () => {
      isMounted = false;
    };
  }, [token, certificateIdFromRoute]);

  // Canvas context setup
  useEffect(() => {
    if (activeSignMilestone && signMode === 'draw') {
      const timer = setTimeout(() => {
        const canvas = canvasRef.current;
        if (canvas) {
          const ctx = canvas.getContext('2d');
          if (ctx) {
            ctx.strokeStyle = '#0d9488';
            ctx.lineWidth = 2.5;
            ctx.lineCap = 'round';
            ctx.lineJoin = 'round';
          }
        }
      }, 50);
      return () => clearTimeout(timer);
    }
  }, [activeSignMilestone, signMode]);

  // Canvas drawing handlers
  const getCanvasCoordinates = (
    canvas: HTMLCanvasElement,
    e: React.MouseEvent<HTMLCanvasElement> | React.TouchEvent<HTMLCanvasElement>
  ) => {
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;

    let clientX = 0;
    let clientY = 0;

    if ('touches' in e) {
      if (e.touches && e.touches.length > 0) {
        clientX = e.touches[0].clientX;
        clientY = e.touches[0].clientY;
      } else if (e.changedTouches && e.changedTouches.length > 0) {
        clientX = e.changedTouches[0].clientX;
        clientY = e.changedTouches[0].clientY;
      }
    } else {
      clientX = (e as React.MouseEvent).clientX;
      clientY = (e as React.MouseEvent).clientY;
    }

    return {
      x: (clientX - rect.left) * scaleX,
      y: (clientY - rect.top) * scaleY,
    };
  };

  const startDrawing = (e: React.MouseEvent<HTMLCanvasElement> | React.TouchEvent<HTMLCanvasElement>) => {
    if ('touches' in e && e.cancelable) {
      e.preventDefault();
    }
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    ctx.strokeStyle = '#0d9488';
    ctx.lineWidth = 2.5;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';

    const coords = getCanvasCoordinates(canvas, e);
    ctx.beginPath();
    ctx.moveTo(coords.x, coords.y);
    lastPointRef.current = coords;
    setIsDrawing(true);
    setHasDrawn(true);
  };

  const draw = (e: React.MouseEvent<HTMLCanvasElement> | React.TouchEvent<HTMLCanvasElement>) => {
    if (!isDrawing) return;
    if ('touches' in e && e.cancelable) {
      e.preventDefault();
    }
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const coords = getCanvasCoordinates(canvas, e);
    const lastPoint = lastPointRef.current;

    if (lastPoint) {
      const midPoint = {
        x: (lastPoint.x + coords.x) / 2,
        y: (lastPoint.y + coords.y) / 2,
      };
      ctx.quadraticCurveTo(lastPoint.x, lastPoint.y, midPoint.x, midPoint.y);
      ctx.stroke();
    } else {
      ctx.lineTo(coords.x, coords.y);
      ctx.stroke();
    }

    lastPointRef.current = coords;
  };

  const stopDrawing = () => {
    setIsDrawing(false);
    lastPointRef.current = null;
  };

  const clearCanvas = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    setHasDrawn(false);
  };

  const generateTypedSignatureDataUrl = (name: string): string => {
    const offscreen = document.createElement('canvas');
    offscreen.width = 500;
    offscreen.height = 140;
    const ctx = offscreen.getContext('2d');
    if (!ctx) return '';

    ctx.clearRect(0, 0, offscreen.width, offscreen.height);
    ctx.font = 'italic 34px "Brush Script MT", "Caveat", "Segoe Script", cursive, serif';
    ctx.fillStyle = '#0f766e';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText(name || 'Authorized Signer', offscreen.width / 2, offscreen.height / 2);

    ctx.strokeStyle = 'rgba(15, 118, 110, 0.4)';
    ctx.lineWidth = 1.5;
    ctx.beginPath();
    ctx.moveTo(40, offscreen.height / 2 + 25);
    ctx.lineTo(offscreen.width - 40, offscreen.height / 2 + 25);
    ctx.stroke();

    return offscreen.toDataURL('image/png');
  };

  // Auth Handlers
  const handlePasswordLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!loginEmail.trim() || !loginPassword.trim()) {
      setAuthError('Please enter your email/username and password');
      return;
    }
    setAuthSubmitting(true);
    setAuthError(null);
    setAuthSuccessMsg(null);
    try {
      const res = await api.post<any>('/api/v1/client-auth/login', {
        email: loginEmail.trim(),
        password: loginPassword,
      });
      const data = res.data || res;
      if (data.token) {
        setSearchParams({ token: data.token });
      }
    } catch (err: any) {
      setAuthError(err.message || 'Invalid client credentials or inactive portal access.');
    } finally {
      setAuthSubmitting(false);
    }
  };

  const handleMagicLinkRequest = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!magicEmail.trim()) {
      setAuthError('Please enter your account email address');
      return;
    }
    setAuthSubmitting(true);
    setAuthError(null);
    setAuthSuccessMsg(null);
    try {
      const res = await api.post<any>('/api/v1/client-auth/magic-link', {
        email: magicEmail.trim(),
      });
      const data = res.data || res;
      if (data.portal_token) {
        setSearchParams({ token: data.portal_token });
      } else {
        setAuthSuccessMsg('Access link verified! Redirecting to your portal...');
      }
    } catch (err: any) {
      setAuthError(err.message || 'No client account found for this email address.');
    } finally {
      setAuthSubmitting(false);
    }
  };

  const handleDirectTokenSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!directTokenInput.trim()) {
      setAuthError('Please enter your portal access token');
      return;
    }
    setAuthSubmitting(true);
    setAuthError(null);
    try {
      const res = await api.post<any>('/api/v1/client-auth/magic-link', {
        token: directTokenInput.trim(),
      });
      const data = res.data || res;
      if (data.token) {
        setSearchParams({ token: data.token });
      }
    } catch (err: any) {
      setAuthError(err.message || 'Invalid or expired portal token.');
    } finally {
      setAuthSubmitting(false);
    }
  };

  const handleLogout = async () => {
    try {
      await api.post('/api/v1/client-auth/logout', {});
    } catch {}
    if (typeof document !== 'undefined') {
      document.cookie = 'tsos_client_token=; Max-Age=0; path=/;';
    }
    setSearchParams({});
    setDashboardData(null);
    setError(null);
  };

  const handleCopyLink = () => {
    navigator.clipboard.writeText(window.location.href);
    setCopiedLink(true);
    setTimeout(() => setCopiedLink(false), 2500);
  };

  const handleCopyCertId = (certId: string) => {
    navigator.clipboard.writeText(certId);
    setCopiedCertId(true);
    setTimeout(() => setCopiedCertId(false), 2500);
  };

  // Milestone Signature Handlers
  const handleOpenSignModal = (m: MilestoneDeliverable) => {
    setActiveSignMilestone(m);
    setDeclarationAgreed(false);
    setSignError(null);
    setHasDrawn(false);
    if (dashboardData?.client?.name) {
      setSignerName(dashboardData.client.name);
    }
  };

  const handleCloseSignModal = () => {
    setActiveSignMilestone(null);
    setSignError(null);
    clearCanvas();
  };

  const handleSubmitSignature = async () => {
    if (!activeSignMilestone || !token) return;
    if (!signerName.trim() || signerName.trim().length < 2) {
      setSignError('Please enter your full legal name.');
      return;
    }
    if (!declarationAgreed) {
      setSignError('You must review and check the formal acceptance declaration.');
      return;
    }

    let signatureData = '';
    if (signMode === 'draw') {
      const canvas = canvasRef.current;
      if (!canvas || !hasDrawn) {
        setSignError('Please draw your digital signature on the pad above.');
        return;
      }
      signatureData = canvas.toDataURL('image/png');
    } else {
      signatureData = generateTypedSignatureDataUrl(signerName.trim());
    }

    setIsSubmittingSignature(true);
    setSignError(null);

    try {
      const res = await api.post<any>(`/api/v1/portal/${token}/milestones/${activeSignMilestone.id}/approve`, {
        signer_name: signerName.trim(),
        signer_title: signerTitle.trim() || 'Authorized Client Representative',
        signature_data: signatureData,
        declaration_agreed: true,
      });

      const cert = res.data?.certificate || res.certificate || res.data;

      // Update local milestones in dashboardData
      if (dashboardData) {
        const updatedMilestones = dashboardData.milestones.map((m) => {
          if (m.id === activeSignMilestone.id) {
            return {
              ...m,
              status: 'completed',
              is_approved: true,
              certificate_id: cert.certificate_id,
              certificate_hash: cert.certificate_hash,
              approved_at: cert.approved_at,
              approved_by_name: cert.signer_name,
              approved_by_title: cert.signer_title,
              signer_ip: cert.signer_ip || null,
              approved_signature_data: cert.signature_data,
            };
          }
          return m;
        });

        // Add certificate file if not present
        const updatedFiles = [...dashboardData.files];
        if (!updatedFiles.some((f) => f.name.includes(cert.certificate_id))) {
          updatedFiles.push({
            id: 'fil_cert_' + cert.certificate_id,
            name: `Handover Certificate ${cert.certificate_id}.pdf`,
            file_type: 'certificate',
            category: 'legal',
            size_formatted: '142 KB',
            project_name: activeSignMilestone.project_name || 'Project',
            description: `${activeSignMilestone.title} formal E-Signature sign-off certificate.`,
            url: `#/portal/certificate/${cert.certificate_id}`,
            created_at: cert.approved_at,
          });
        }

        setDashboardData({
          ...dashboardData,
          milestones: updatedMilestones,
          files: updatedFiles,
        });
      }

      handleCloseSignModal();
      setViewingCertificate(cert);
    } catch (err: any) {
      setSignError(err.message || 'Failed to submit milestone sign-off. Please try again.');
    } finally {
      setIsSubmittingSignature(false);
    }
  };

  // Render: Standalone Certificate View
  if (standaloneCert) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center p-4 sm:p-8">
        <div className="w-full max-w-3xl">
          <CertificateCard
            cert={standaloneCert}
            onClose={() => setStandaloneCert(null)}
            isStandalone={true}
            copiedCertId={copiedCertId}
            onCopyCertId={handleCopyCertId}
          />
        </div>
      </div>
    );
  }

  // Render: Loading Screen
  if (loading) {
    return (
      <div className="min-h-screen bg-slate-950 flex flex-col items-center justify-center text-white space-y-4">
        <div className="w-12 h-12 border-4 border-teal-500/20 border-t-teal-400 rounded-full animate-spin" />
        <p className="text-sm font-medium text-slate-400">Loading secure client account portal...</p>
      </div>
    );
  }

  // Render: Unauthenticated Login / Magic-Link View
  if (!dashboardData) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-slate-950 via-slate-900 to-slate-950 flex items-center justify-center p-4 sm:p-6 text-slate-100">
        <div className="w-full max-w-md space-y-6">
          {/* Brand Header */}
          <div className="text-center space-y-2">
            <div className="inline-flex items-center space-x-2 px-3 py-1 rounded-full text-xs font-bold bg-teal-500/15 text-teal-300 border border-teal-500/30">
              <ShieldCheck className="w-4 h-4 text-teal-400" />
              <span>SECURE CLIENT PORTAL</span>
            </div>
            <h1 className="text-2xl sm:text-3xl font-extrabold text-white tracking-tight">
              TechoSolution OS
            </h1>
            <p className="text-xs text-slate-400 max-w-xs mx-auto">
              Client Account Onboarding, Deliverables, Milestones & Invoice Hub
            </p>
          </div>

          {/* Login Card */}
          <Card className="bg-slate-900/90 border border-slate-800 shadow-2xl backdrop-blur-sm rounded-2xl">
            <CardContent className="p-6 space-y-5">
              {/* Method Switcher Tabs */}
              <div className="flex rounded-xl bg-slate-950 p-1 border border-slate-800 text-xs font-medium">
                <button
                  type="button"
                  onClick={() => { setAuthMode('password'); setAuthError(null); }}
                  className={`flex-1 py-1.5 rounded-lg transition-all ${
                    authMode === 'password' ? 'bg-teal-600 text-white font-bold shadow' : 'text-slate-400 hover:text-white'
                  }`}
                >
                  Password
                </button>
                <button
                  type="button"
                  onClick={() => { setAuthMode('magic'); setAuthError(null); }}
                  className={`flex-1 py-1.5 rounded-lg transition-all ${
                    authMode === 'magic' ? 'bg-teal-600 text-white font-bold shadow' : 'text-slate-400 hover:text-white'
                  }`}
                >
                  Magic Link
                </button>
                <button
                  type="button"
                  onClick={() => { setAuthMode('token'); setAuthError(null); }}
                  className={`flex-1 py-1.5 rounded-lg transition-all ${
                    authMode === 'token' ? 'bg-teal-600 text-white font-bold shadow' : 'text-slate-400 hover:text-white'
                  }`}
                >
                  Token
                </button>
              </div>

              {/* Error & Success Banners */}
              {authError && (
                <div className="p-3 bg-red-950/60 border border-red-800/80 rounded-xl text-xs text-red-300 flex items-start space-x-2">
                  <AlertCircle className="w-4 h-4 text-red-400 shrink-0 mt-0.5" />
                  <span>{authError}</span>
                </div>
              )}
              {error && !authError && (
                <div className="p-3 bg-amber-950/60 border border-amber-800/80 rounded-xl text-xs text-amber-300 flex items-start space-x-2">
                  <AlertCircle className="w-4 h-4 text-amber-400 shrink-0 mt-0.5" />
                  <span>{error}</span>
                </div>
              )}
              {authSuccessMsg && (
                <div className="p-3 bg-emerald-950/60 border border-emerald-800/80 rounded-xl text-xs text-emerald-300 flex items-start space-x-2">
                  <CheckCircle2 className="w-4 h-4 text-emerald-400 shrink-0 mt-0.5" />
                  <span>{authSuccessMsg}</span>
                </div>
              )}

              {/* Tab 1: Password Login */}
              {authMode === 'password' && (
                <form onSubmit={handlePasswordLogin} className="space-y-4">
                  <div className="space-y-1.5">
                    <label className="text-xs font-semibold text-slate-300">Client Email / Username</label>
                    <Input
                      type="text"
                      placeholder="e.g. client@company.com or dkodaa"
                      value={loginEmail}
                      onChange={(e) => setLoginEmail(e.target.value)}
                      className="bg-slate-950 border-slate-800 text-white placeholder:text-slate-600"
                      required
                    />
                  </div>
                  <div className="space-y-1.5">
                    <label className="text-xs font-semibold text-slate-300">Portal Password</label>
                    <div className="relative">
                      <Input
                        type={showPassword ? 'text' : 'password'}
                        placeholder="Enter onboarding password"
                        value={loginPassword}
                        onChange={(e) => setLoginPassword(e.target.value)}
                        className="bg-slate-950 border-slate-800 text-white placeholder:text-slate-600 pr-9"
                        required
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-2.5 top-2.5 text-slate-400 hover:text-white"
                      >
                        {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    </div>
                  </div>
                  <Button
                    type="submit"
                    disabled={authSubmitting}
                    className="w-full bg-teal-600 hover:bg-teal-500 text-white font-bold text-xs h-10 shadow-lg shadow-teal-900/30"
                  >
                    {authSubmitting ? 'Authenticating...' : 'Sign In to Client Portal'}
                  </Button>
                </form>
              )}

              {/* Tab 2: Magic Link Access */}
              {authMode === 'magic' && (
                <form onSubmit={handleMagicLinkRequest} className="space-y-4">
                  <p className="text-xs text-slate-400 leading-relaxed">
                    Enter the email associated with your client account to instantly generate or retrieve your authenticated portal access link.
                  </p>
                  <div className="space-y-1.5">
                    <label className="text-xs font-semibold text-slate-300">Account Email</label>
                    <Input
                      type="email"
                      placeholder="client@company.com"
                      value={magicEmail}
                      onChange={(e) => setMagicEmail(e.target.value)}
                      className="bg-slate-950 border-slate-800 text-white placeholder:text-slate-600"
                      required
                    />
                  </div>
                  <Button
                    type="submit"
                    disabled={authSubmitting}
                    className="w-full bg-teal-600 hover:bg-teal-500 text-white font-bold text-xs h-10 shadow-lg shadow-teal-900/30 flex items-center justify-center space-x-1.5"
                  >
                    <Send className="w-3.5 h-3.5" />
                    <span>{authSubmitting ? 'Generating...' : 'Get Instant Portal Link'}</span>
                  </Button>
                </form>
              )}

              {/* Tab 3: Direct Token Access */}
              {authMode === 'token' && (
                <form onSubmit={handleDirectTokenSubmit} className="space-y-4">
                  <p className="text-xs text-slate-400 leading-relaxed">
                    Have a direct portal token (e.g. from an onboarding invite or WhatsApp update)? Paste it here:
                  </p>
                  <div className="space-y-1.5">
                    <label className="text-xs font-semibold text-slate-300">Portal Access Token</label>
                    <Input
                      type="text"
                      placeholder="tsos_clt_... or tsos_portal_..."
                      value={directTokenInput}
                      onChange={(e) => setDirectTokenInput(e.target.value)}
                      className="bg-slate-950 border-slate-800 text-white placeholder:text-slate-600 font-mono text-xs"
                      required
                    />
                  </div>
                  <Button
                    type="submit"
                    disabled={authSubmitting}
                    className="w-full bg-teal-600 hover:bg-teal-500 text-white font-bold text-xs h-10 shadow-lg shadow-teal-900/30"
                  >
                    {authSubmitting ? 'Verifying...' : 'Unlock Client Portal'}
                  </Button>
                </form>
              )}
            </CardContent>
          </Card>

          {/* Agency Support Concierge Note */}
          <div className="text-center text-xs text-slate-500 space-y-1">
            <p>Need access credentials or having trouble logging in?</p>
            <p>
              Contact your TechoSolution Agency Project Manager or{' '}
              <a
                href="https://wa.me/923000000000?text=Hello%20TechoSolution%20Support,%20I%20need%20help%20accessing%20my%20Client%20Portal"
                target="_blank"
                rel="noreferrer"
                className="text-teal-400 hover:underline"
              >
                reach billing support via WhatsApp
              </a>.
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Filter projects & deliverables according to selector
  const allProjects = dashboardData.projects || [];
  const activeProjects = allProjects.filter((p) => (p.status || '').toLowerCase() !== 'completed' && (p.status || '').toLowerCase() !== 'archived');
  const completedProjects = allProjects.filter((p) => (p.status || '').toLowerCase() === 'completed' || (p.status || '').toLowerCase() === 'delivered');
  const displayedProjects = projectScopeView === 'completed'
    ? (completedProjects.length > 0 ? completedProjects : allProjects)
    : (projectScopeView === 'active' ? (activeProjects.length > 0 ? activeProjects : allProjects) : allProjects);
  const primaryProject = displayedProjects[0] || allProjects[0];

  const filteredMilestones = (dashboardData.milestones || []).filter((m) =>
    selectedProjectId === 'all' ? true : m.project_id === selectedProjectId
  );
  const filteredTasks = (dashboardData.tasks || []).filter((t) => {
    if (selectedProjectId !== 'all' && t.project_id !== selectedProjectId) return false;
    if (taskStatusFilter !== 'all') {
      if (taskStatusFilter === 'completed' && !(t.status === 'done' || t.status === 'completed')) return false;
      if (taskStatusFilter === 'in_progress' && t.status !== 'in_progress') return false;
      if (taskStatusFilter === 'in_review' && t.status !== 'in_review') return false;
      if (taskStatusFilter === 'todo' && !(t.status === 'todo' || t.status === 'pending')) return false;
    }
    if (taskMilestoneFilter !== 'all' && t.milestone_id !== taskMilestoneFilter) return false;
    if (taskSearchQuery.trim()) {
      const q = taskSearchQuery.toLowerCase();
      const matchTitle = t.title?.toLowerCase().includes(q);
      const matchDesc = t.description?.toLowerCase().includes(q);
      const matchMilestone = t.milestone_title?.toLowerCase().includes(q);
      if (!matchTitle && !matchDesc && !matchMilestone) return false;
    }
    return true;
  });
  const filteredProposals = (dashboardData.proposals || []).filter((p) =>
    selectedProjectId === 'all' ? true : p.project_id === selectedProjectId
  );
  const filteredFiles = (dashboardData.files || []).filter((f) =>
    fileFilter === 'all' ? true : f.category === fileFilter
  );

  const completedMilestonesCount = (dashboardData.milestones || []).filter((m) => m.is_approved || m.status === 'completed').length;
  const totalMilestonesCount = (dashboardData.milestones || []).length;
  const averageProgress = activeProjects.length > 0
    ? Math.round(activeProjects.reduce((acc, p) => acc + (p.progress_percent || 0), 0) / activeProjects.length)
    : 0;

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100 flex flex-col">
      {/* Top Navigation & Client Branding Banner */}
      <header className="border-b border-slate-800/80 bg-slate-900/80 backdrop-blur sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3.5 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div className="flex items-center space-x-3">
            <div className="w-9 h-9 rounded-xl bg-teal-500/20 border border-teal-500/40 flex items-center justify-center text-teal-400 font-black text-sm">
              TS
            </div>
            <div>
              <div className="flex items-center space-x-2">
                <span className="font-extrabold text-white text-base tracking-tight">
                  TechoSolution
                </span>
                <span className="px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider bg-teal-500/15 text-teal-300 border border-teal-500/30">
                  Client Portal
                </span>
              </div>
              <p className="text-xs text-slate-400">
                {dashboardData.client.company || dashboardData.client.name} &bull; Lifecycle Delivery Hub
              </p>
            </div>
          </div>

          <div className="flex items-center space-x-2 self-end sm:self-auto">
            {dashboardData.agency_contact?.whatsapp_link && (
              <a
                href={dashboardData.agency_contact.whatsapp_link}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center space-x-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold bg-emerald-500/15 text-emerald-300 border border-emerald-500/30 hover:bg-emerald-500/25 transition-colors"
                title="Direct WhatsApp Support Concierge"
              >
                <MessageCircle className="w-3.5 h-3.5 text-emerald-400" />
                <span className="hidden sm:inline">WhatsApp Concierge</span>
              </a>
            )}

            <Button
              variant="outline"
              size="sm"
              onClick={handleCopyLink}
              className="text-xs h-8 border-slate-800 text-slate-300 hover:bg-slate-800 flex items-center space-x-1"
              title="Copy secure portal link"
            >
              {copiedLink ? <Check className="w-3.5 h-3.5 text-emerald-400" /> : <Copy className="w-3.5 h-3.5" />}
              <span className="hidden sm:inline">{copiedLink ? 'Link Copied' : 'Share Link'}</span>
            </Button>

            <Button
              variant="ghost"
              size="sm"
              onClick={handleLogout}
              className="text-xs h-8 text-slate-400 hover:text-red-400 hover:bg-red-950/20"
              title="Sign Out"
            >
              <LogOut className="w-3.5 h-3.5 sm:mr-1" />
              <span className="hidden sm:inline">Sign Out</span>
            </Button>
          </div>
        </div>

        {/* Tab Navigation Strip */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex overflow-x-auto no-scrollbar space-x-1 sm:space-x-2 pt-1 border-t border-slate-800/50">
          {[
            { id: 'overview', label: 'Overview & Timeline', icon: Layers },
            { id: 'tasks', label: 'All Tasks & Work Log', icon: CheckCircle2, count: (dashboardData.tasks || []).length },
            { id: 'milestones', label: 'Milestones & Sign-Off', icon: FileCheck, count: dashboardData.milestones?.length },
            { id: 'proposals', label: 'Proposals & Scope', icon: FileText, count: dashboardData.proposals?.length },
            { id: 'deliverables', label: 'Deliverables & Files', icon: FolderKanban, count: dashboardData.files?.length },
            { id: 'invoices', label: 'Payment Terms & Invoices', icon: DollarSign, count: dashboardData.invoices?.length },
          ].map((tab) => {
            const Icon = tab.icon;
            const isActive = activeTab === tab.id;
            return (
              <button
                key={tab.id}
                type="button"
                onClick={() => setActiveTab(tab.id as any)}
                className={`flex items-center space-x-2 px-3.5 py-2.5 text-xs font-semibold border-b-2 whitespace-nowrap transition-colors ${
                  isActive
                    ? 'border-teal-400 text-teal-300 bg-teal-500/5'
                    : 'border-transparent text-slate-400 hover:text-slate-200 hover:border-slate-700'
                }`}
              >
                <Icon className={`w-4 h-4 ${isActive ? 'text-teal-400' : 'text-slate-500'}`} />
                <span>{tab.label}</span>
                {tab.count !== undefined && tab.count > 0 && (
                  <span className={`px-1.5 py-0.2 rounded-full text-[10px] font-mono ${
                    isActive ? 'bg-teal-500/20 text-teal-300' : 'bg-slate-800 text-slate-400'
                  }`}>
                    {tab.count}
                  </span>
                )}
              </button>
            );
          })}
        </div>
      </header>

      {/* Main Workspace Body */}
      <main className="max-w-7xl mx-auto w-full px-4 sm:px-6 lg:px-8 py-6 space-y-6 flex-1">
        {/* Executive Peace-of-Mind Assurance Banner ("Zero Tension, Easy Rhy") */}
        <div className="bg-gradient-to-r from-emerald-950/40 via-slate-900 to-teal-950/40 border border-emerald-500/30 rounded-2xl p-4 sm:p-5 shadow-2xl relative overflow-hidden">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
            <div className="flex items-start space-x-3.5">
              <div className="p-2.5 rounded-xl bg-emerald-500/10 border border-emerald-500/30 text-emerald-400 shrink-0 mt-0.5">
                <ShieldCheck className="w-6 h-6 animate-pulse" />
              </div>
              <div className="space-y-1">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="text-xs font-extrabold uppercase tracking-wider text-emerald-400">
                    Executive Health: 100% On-Track
                  </span>
                  <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-teal-500/15 text-teal-300 border border-teal-500/30">
                    SLA &amp; Delivery Protected
                  </span>
                  <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-emerald-500/15 text-emerald-300 border border-emerald-500/30">
                    No Tension &bull; Relax While We Ship
                  </span>
                </div>
                <p className="text-xs text-slate-300 max-w-2xl leading-relaxed">
                  Welcome back, <strong className="text-white">{dashboardData.client.name}</strong>. Your dedicated engineering team is actively executing deliverables. Every milestone is backed by strict automated QA, cryptographic sign-off sealing, and guaranteed on-time delivery.
                </p>
              </div>
            </div>

            {/* Leadership Direct Hotline */}
            <div className="flex items-center space-x-2 shrink-0 self-end md:self-auto bg-slate-950/70 p-2.5 rounded-xl border border-slate-800">
              <div className="text-right hidden sm:block pr-2">
                <span className="text-[10px] uppercase font-bold text-slate-400 block">Engineering Lead</span>
                <span className="text-xs font-bold text-white">Hamza CS</span>
              </div>
              {dashboardData.agency_contact?.whatsapp_link ? (
                <a
                  href={dashboardData.agency_contact.whatsapp_link}
                  target="_blank"
                  rel="noreferrer"
                  className="px-3 py-1.5 bg-emerald-600 hover:bg-emerald-500 text-white rounded-lg text-xs font-bold flex items-center space-x-1.5 transition-colors shadow"
                  title="Direct WhatsApp with Lead Architect"
                >
                  <MessageCircle className="w-3.5 h-3.5" />
                  <span>WhatsApp Lead</span>
                </a>
              ) : (
                <a
                  href="https://wa.me/923000000000"
                  target="_blank"
                  rel="noreferrer"
                  className="px-3 py-1.5 bg-emerald-600 hover:bg-emerald-500 text-white rounded-lg text-xs font-bold flex items-center space-x-1.5 transition-colors shadow"
                >
                  <MessageCircle className="w-3.5 h-3.5" />
                  <span>WhatsApp Lead</span>
                </a>
              )}
              <a
                href={`mailto:${dashboardData.agency_contact?.email || 'techosolution9@gmail.com'}`}
                className="px-3 py-1.5 bg-slate-800 hover:bg-slate-700 text-slate-200 rounded-lg text-xs font-bold flex items-center space-x-1 transition-colors"
                title="Direct Email"
              >
                <Send className="w-3.5 h-3.5" />
                <span className="hidden sm:inline">Email</span>
              </a>
            </div>
          </div>
        </div>

        {/* Project Scope & Historical Portfolio Switcher */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 bg-slate-900 border border-slate-800 p-3.5 rounded-2xl shadow-lg">
          <div className="flex items-center space-x-2 text-xs">
            <span className="text-slate-400 font-bold uppercase text-[10px] tracking-wider flex items-center space-x-1">
              <FolderKanban className="w-3.5 h-3.5 text-teal-400" />
              <span>Project View:</span>
            </span>
            <div className="flex items-center space-x-1 bg-slate-950 p-1 border border-slate-800 rounded-xl">
              <button
                type="button"
                onClick={() => { setProjectScopeView('active'); setSelectedProjectId('all'); }}
                className={`px-3 py-1 rounded-lg text-xs font-bold transition-colors ${
                  projectScopeView === 'active'
                    ? 'bg-teal-600 text-white shadow'
                    : 'text-slate-400 hover:text-white'
                }`}
              >
                Active Projects ({activeProjects.length})
              </button>
              {completedProjects.length > 0 && (
                <button
                  type="button"
                  onClick={() => { setProjectScopeView('completed'); setSelectedProjectId('all'); }}
                  className={`px-3 py-1 rounded-lg text-xs font-bold flex items-center space-x-1.5 transition-colors ${
                    projectScopeView === 'completed'
                      ? 'bg-emerald-600 text-white shadow'
                      : 'text-slate-400 hover:text-white'
                  }`}
                >
                  <History className="w-3 h-3" />
                  <span>Completed Portfolio ({completedProjects.length})</span>
                </button>
              )}
            </div>
          </div>

          {/* Individual Project Scope Filter */}
          {displayedProjects.length > 1 && (
            <div className="flex items-center space-x-1.5 overflow-x-auto text-xs">
              <button
                type="button"
                onClick={() => setSelectedProjectId('all')}
                className={`px-2.5 py-1 rounded-lg text-xs font-medium transition-colors ${
                  selectedProjectId === 'all'
                    ? 'bg-teal-500/20 text-teal-300 border border-teal-500/30'
                    : 'text-slate-400 hover:text-slate-200'
                }`}
              >
                All Scope ({displayedProjects.length})
              </button>
              {displayedProjects.map((p) => (
                <button
                  key={p.id}
                  type="button"
                  onClick={() => setSelectedProjectId(p.id)}
                  className={`px-2.5 py-1 rounded-lg text-xs font-medium whitespace-nowrap transition-colors ${
                    selectedProjectId === p.id
                      ? 'bg-teal-500/20 text-teal-300 border border-teal-500/30'
                      : 'text-slate-400 hover:text-slate-200'
                  }`}
                >
                  {p.name}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* TAB 1: OVERVIEW & TIMELINE */}
        {activeTab === 'overview' && (
          <div className="space-y-6">
            {/* Top Row: Delivery Countdown & KPI Summary */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
              {/* Delivery Countdown Card */}
              <div className="lg:col-span-2 bg-gradient-to-br from-slate-900 to-slate-950 border border-teal-500/30 rounded-2xl p-6 relative overflow-hidden shadow-xl">
                <div className="absolute top-0 right-0 -mt-6 -mr-6 w-32 h-32 rounded-full bg-teal-500/10 blur-xl pointer-events-none" />
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                  <div className="space-y-2">
                    <div className="inline-flex items-center space-x-2 px-2.5 py-1 rounded-full text-[11px] font-bold bg-teal-500/20 text-teal-300 border border-teal-500/30">
                      <Clock className="w-3.5 h-3.5" />
                      <span>DELIVERY COUNTDOWN</span>
                    </div>
                    <h2 className="text-2xl sm:text-3xl font-extrabold text-white tracking-tight">
                      {primaryProject?.delivery_countdown?.formatted || 'Sprint in Progress'}
                    </h2>
                    <p className="text-xs text-slate-400 max-w-md">
                      Targeting delivery deadline:{' '}
                      <strong className="text-slate-200">
                        {primaryProject?.delivery_countdown?.deadline
                          ? new Date(primaryProject.delivery_countdown.deadline).toLocaleDateString('en-US', {
                              month: 'long',
                              day: 'numeric',
                              year: 'numeric',
                            })
                          : 'Ongoing Milestone Cadence'}
                      </strong>
                    </p>
                  </div>

                  <div className="flex items-center space-x-4 self-center sm:self-auto">
                    <ProgressRing progress={averageProgress} size={90} />
                  </div>
                </div>

                {/* Milestone quick progress bar */}
                <div className="mt-5 pt-4 border-t border-slate-800/80 flex flex-col sm:flex-row sm:items-center justify-between gap-2 text-xs">
                  <span className="text-slate-400">
                    Handover Status: <strong className="text-teal-300">{completedMilestonesCount} of {totalMilestonesCount} Milestones Signed</strong>
                  </span>
                  <div className="flex items-center space-x-1.5 text-slate-400">
                    <ShieldCheck className="w-4 h-4 text-emerald-400" />
                    <span>Cryptographically Tamper-Sealed Delivery</span>
                  </div>
                </div>
              </div>

              {/* Financial Snapshot Card */}
              <div className="bg-slate-900 border border-slate-800 rounded-2xl p-6 flex flex-col justify-between space-y-4 shadow-xl">
                <div>
                  <span className="text-[10px] font-bold uppercase tracking-wider text-slate-400">Account Financial Summary</span>
                  <div className="mt-2 space-y-2">
                    <div className="flex justify-between items-center text-xs">
                      <span className="text-slate-400">Total Invoiced:</span>
                      <span className="font-mono font-bold text-white">
                        {dashboardData.financial_summary?.currency || 'USD'} ${dashboardData.financial_summary?.total_invoiced?.toLocaleString()}
                      </span>
                    </div>
                    <div className="flex justify-between items-center text-xs">
                      <span className="text-slate-400">Total Paid:</span>
                      <span className="font-mono font-bold text-emerald-400">
                        {dashboardData.financial_summary?.currency || 'USD'} ${dashboardData.financial_summary?.total_paid?.toLocaleString()}
                      </span>
                    </div>
                    <div className="flex justify-between items-center text-xs pt-1 border-t border-slate-800">
                      <span className="font-bold text-slate-300">Balance Due:</span>
                      <span className={`font-mono font-extrabold text-sm ${
                        (dashboardData.financial_summary?.balance_due || 0) > 0 ? 'text-amber-400' : 'text-emerald-400'
                      }`}>
                        {dashboardData.financial_summary?.currency || 'USD'} ${dashboardData.financial_summary?.balance_due?.toLocaleString()}
                      </span>
                    </div>
                  </div>
                </div>

                {dashboardData.agency_contact?.whatsapp_link && (
                  <a
                    href={dashboardData.agency_contact.whatsapp_link}
                    target="_blank"
                    rel="noreferrer"
                    className="w-full py-2 bg-emerald-600 hover:bg-emerald-500 text-white rounded-xl text-xs font-bold flex items-center justify-center space-x-1.5 transition-colors shadow"
                  >
                    <CreditCard className="w-3.5 h-3.5" />
                    <span>Billing & Escrow Release Inquiry</span>
                  </a>
                )}
              </div>
            </div>

            {/* Interactive 4-Stage Lifecycle Flow */}
            <div className="bg-slate-900 border border-slate-800 rounded-2xl p-6 space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-sm font-bold text-white">Lifecycle Delivery Stages</h3>
                  <p className="text-xs text-slate-400">Transparent progression from onboarding to milestone handover</p>
                </div>
                <Badge variant="outline" className="border-teal-500/40 text-teal-300 text-[10px]">
                  Sprint Active
                </Badge>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 pt-2">
                {[
                  { stage: '1. Discovery & Scope', status: 'completed', desc: 'Scope agreed, architecture ratified, baseline repo established.' },
                  { stage: '2. UX/UI & Prototyping', status: 'completed', desc: 'Figma mockups approved, interactive workflows signed off.' },
                  { stage: '3. Production Build', status: 'in_progress', desc: 'Milestone execution, automated CI/CD, and regression tests.' },
                  { stage: '4. Final Acceptance', status: 'pending', desc: 'Formal E-Signature handover, certificates, and IP transfer.' },
                ].map((s, idx) => (
                  <div
                    key={idx}
                    className={`p-3.5 rounded-xl border text-xs space-y-1.5 ${
                      s.status === 'completed'
                        ? 'bg-teal-950/20 border-teal-800/60 text-slate-300'
                        : s.status === 'in_progress'
                        ? 'bg-slate-800/90 border-teal-500 text-white shadow-lg'
                        : 'bg-slate-950/40 border-slate-800/60 text-slate-500'
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <span className="font-bold">{s.stage}</span>
                      {s.status === 'completed' && <CheckCircle2 className="w-3.5 h-3.5 text-teal-400" />}
                      {s.status === 'in_progress' && (
                        <span className="w-2 h-2 rounded-full bg-teal-400 animate-pulse" />
                      )}
                    </div>
                    <p className="text-[11px] leading-relaxed text-slate-400">{s.desc}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* Active Milestones & Client Tasks Grid */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Milestones in this project */}
              <div className="bg-slate-900 border border-slate-800 rounded-2xl p-6 space-y-4">
                <div className="flex items-center justify-between">
                  <h3 className="text-sm font-bold text-white flex items-center space-x-2">
                    <FileCheck className="w-4 h-4 text-teal-400" />
                    <span>Project Deliverable Milestones</span>
                  </h3>
                  <button
                    type="button"
                    onClick={() => setActiveTab('milestones')}
                    className="text-xs text-teal-400 hover:underline flex items-center space-x-1"
                  >
                    <span>View all</span>
                    <ChevronRight className="w-3 h-3" />
                  </button>
                </div>

                <div className="space-y-3">
                  {filteredMilestones.slice(0, 3).map((ms) => (
                    <div
                      key={ms.id}
                      className="p-3 bg-slate-950/60 border border-slate-800/80 rounded-xl flex items-center justify-between text-xs hover:border-slate-700 transition-colors"
                    >
                      <div className="space-y-1 pr-3">
                        <div className="flex items-center space-x-2">
                          <span className="font-bold text-white">{ms.title}</span>
                          {ms.is_approved && (
                            <Badge className="bg-teal-500/20 text-teal-300 border-teal-500/30 text-[9px]">
                              Signed
                            </Badge>
                          )}
                        </div>
                        <p className="text-[11px] text-slate-400 truncate max-w-sm">
                          {ms.description || 'Milestone deliverables and review scope.'}
                        </p>
                      </div>

                      {ms.certificate_id ? (
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => setViewingCertificate({
                            certificate_id: ms.certificate_id,
                            certificate_hash: ms.certificate_hash || '',
                            project_id: ms.project_id || '',
                            project_name: ms.project_name || '',
                            client_name: dashboardData.client.name,
                            milestone_id: ms.id,
                            milestone_title: ms.title,
                            milestone_order: ms.order_index || 1,
                            completed_at: ms.approved_at || '',
                            approved_at: ms.approved_at || '',
                            signer_name: ms.approved_by_name || 'Client',
                            signer_title: ms.approved_by_title || 'Authorized Client Representative',
                            signer_ip: ms.signer_ip || '127.0.0.1',
                            signature_data: ms.approved_signature_data || '',
                            verification_status: 'VALID_TAMPER_SEALED',
                            issued_by: 'Techosolution Project OS Signature Service',
                          })}
                          className="text-[11px] h-7 bg-teal-500/15 text-teal-300 hover:bg-teal-500/25 shrink-0"
                        >
                          Certificate
                        </Button>
                      ) : (
                        <Badge variant="outline" className="text-[10px] uppercase border-slate-700 text-slate-400 shrink-0">
                          {ms.status.replace('_', ' ')}
                        </Badge>
                      )}
                    </div>
                  ))}
                </div>
              </div>

              {/* Client-Visible Tasks */}
              <div className="bg-slate-900 border border-slate-800 rounded-2xl p-6 space-y-4">
                <div className="flex items-center justify-between">
                  <h3 className="text-sm font-bold text-white flex items-center space-x-2">
                    <Clock className="w-4 h-4 text-teal-400" />
                    <span>Real-Time Deliverable Tasks</span>
                  </h3>
                  <button
                    type="button"
                    onClick={() => setActiveTab('tasks')}
                    className="text-xs text-teal-400 hover:underline flex items-center space-x-1"
                  >
                    <span>View all ({filteredTasks.length})</span>
                    <ChevronRight className="w-3 h-3" />
                  </button>
                </div>

                <div className="space-y-2.5 max-h-[260px] overflow-y-auto pr-1 no-scrollbar">
                  {filteredTasks.length === 0 ? (
                    <p className="text-xs text-slate-500 py-4 text-center">No active client-facing tasks logged.</p>
                  ) : (
                    filteredTasks.slice(0, 5).map((t) => (
                      <div
                        key={t.id}
                        className="p-3 bg-slate-950/60 border border-slate-800/80 rounded-xl flex items-center justify-between text-xs"
                      >
                        <div className="space-y-0.5">
                          <p className="font-semibold text-white">{t.title}</p>
                          <p className="text-[11px] text-slate-400">{t.project_name}</p>
                        </div>
                        <Badge
                          variant="outline"
                          className={`text-[10px] uppercase ${
                            t.status === 'done'
                              ? 'border-emerald-500/40 text-emerald-300 bg-emerald-500/10'
                              : t.status === 'in_review'
                              ? 'border-amber-500/40 text-amber-300 bg-amber-500/10'
                              : 'border-slate-700 text-slate-400'
                          }`}
                        >
                          {t.status.replace('_', ' ')}
                        </Badge>
                      </div>
                    ))
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* TAB 2: ALL TASKS & WORK LOG ("Sab Task A-to-Z") */}
        {activeTab === 'tasks' && (
          <div className="space-y-6">
            {/* Header & Reassurance */}
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
              <div>
                <h2 className="text-lg font-bold text-white flex items-center space-x-2">
                  <CheckCircle2 className="w-5 h-5 text-teal-400" />
                  <span>All Tasks &amp; Engineering Work Log</span>
                </h2>
                <p className="text-xs text-slate-400">
                  A-to-Z deliverable transparency &bull; Track feature progression from sprint backlog to production delivery
                </p>
              </div>

              <div className="inline-flex items-center space-x-1.5 px-3 py-1 rounded-full bg-emerald-500/10 border border-emerald-500/30 text-emerald-300 text-xs font-semibold">
                <ShieldCheck className="w-4 h-4 text-emerald-400" />
                <span>Internal Rates &amp; Costs Redacted for Executive Clarity</span>
              </div>
            </div>

            {/* Quick Metrics Strip */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3.5">
              <div className="p-4 bg-slate-900 border border-slate-800 rounded-2xl space-y-1 shadow-lg">
                <span className="text-[10px] uppercase font-bold text-slate-400 block tracking-wider">Total Deliverables</span>
                <p className="font-mono text-2xl font-black text-white">{filteredTasks.length}</p>
                <span className="text-[10px] text-slate-500">Across active scope</span>
              </div>
              <div className="p-4 bg-slate-900 border border-slate-800 rounded-2xl space-y-1 shadow-lg">
                <span className="text-[10px] uppercase font-bold text-slate-400 block tracking-wider">Completed &amp; Verified</span>
                <p className="font-mono text-2xl font-black text-emerald-400">
                  {filteredTasks.filter((t) => t.status === 'done' || t.status === 'completed').length}
                </p>
                <span className="text-[10px] text-emerald-400/80 font-medium">Production ready</span>
              </div>
              <div className="p-4 bg-slate-900 border border-slate-800 rounded-2xl space-y-1 shadow-lg">
                <span className="text-[10px] uppercase font-bold text-slate-400 block tracking-wider">In Active Build</span>
                <p className="font-mono text-2xl font-black text-teal-400">
                  {filteredTasks.filter((t) => t.status === 'in_progress').length}
                </p>
                <span className="text-[10px] text-teal-400/80 font-medium">Under active coding</span>
              </div>
              <div className="p-4 bg-slate-900 border border-slate-800 rounded-2xl space-y-1 shadow-lg">
                <span className="text-[10px] uppercase font-bold text-slate-400 block tracking-wider">Under Review / QA</span>
                <p className="font-mono text-2xl font-black text-amber-400">
                  {filteredTasks.filter((t) => t.status === 'in_review').length}
                </p>
                <span className="text-[10px] text-amber-400/80 font-medium">Quality assurance stage</span>
              </div>
            </div>

            {/* Filter & Search Bar */}
            <div className="bg-slate-900 border border-slate-800 rounded-2xl p-4 flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-3 shadow-lg">
              <div className="relative flex-1">
                <Search className="w-4 h-4 text-slate-500 absolute left-3.5 top-1/2 -translate-y-1/2" />
                <input
                  type="text"
                  value={taskSearchQuery}
                  onChange={(e) => setTaskSearchQuery(e.target.value)}
                  placeholder="Search deliverables by title, milestone or scope..."
                  className="w-full pl-10 pr-4 py-2 bg-slate-950 border border-slate-800 rounded-xl text-xs text-white placeholder-slate-500 focus:outline-none focus:border-teal-500 transition-colors"
                />
              </div>

              <div className="flex flex-wrap items-center gap-2">
                {/* Status Pills */}
                <div className="flex items-center space-x-1 bg-slate-950 p-1 border border-slate-800 rounded-xl text-xs">
                  {[
                    { id: 'all', label: 'All' },
                    { id: 'in_progress', label: 'In Progress' },
                    { id: 'in_review', label: 'Review' },
                    { id: 'completed', label: 'Completed' },
                    { id: 'todo', label: 'Backlog' },
                  ].map((st) => (
                    <button
                      key={st.id}
                      type="button"
                      onClick={() => setTaskStatusFilter(st.id)}
                      className={`px-3 py-1 rounded-lg text-[11px] font-bold transition-colors ${
                        taskStatusFilter === st.id
                          ? 'bg-teal-600 text-white shadow'
                          : 'text-slate-400 hover:text-white'
                      }`}
                    >
                      {st.label}
                    </button>
                  ))}
                </div>

                {/* Milestone Filter Dropdown */}
                {filteredMilestones.length > 0 && (
                  <select
                    value={taskMilestoneFilter}
                    onChange={(e) => setTaskMilestoneFilter(e.target.value)}
                    aria-label="Filter tasks by milestone deliverable"
                    className="bg-slate-950 border border-slate-800 rounded-xl px-3 py-2 text-xs text-slate-300 focus:outline-none focus:border-teal-500"
                  >
                    <option value="all">All Milestones</option>
                    {filteredMilestones.map((m) => (
                      <option key={m.id} value={m.id}>
                        {m.title}
                      </option>
                    ))}
                  </select>
                )}
              </div>
            </div>

            {/* Task Cards Grid */}
            <div className="space-y-3">
              {filteredTasks.length === 0 ? (
                <div className="p-12 text-center bg-slate-900 border border-slate-800 rounded-2xl space-y-2">
                  <CheckCircle2 className="w-8 h-8 text-slate-600 mx-auto" />
                  <p className="text-sm font-semibold text-slate-300">No deliverables found matching filter.</p>
                  <p className="text-xs text-slate-500">Try clearing the search query or status filter.</p>
                </div>
              ) : (
                filteredTasks.map((task) => (
                  <div
                    key={task.id}
                    className="p-5 bg-slate-900 border border-slate-800 rounded-2xl flex flex-col md:flex-row md:items-center justify-between gap-4 hover:border-slate-700 transition-colors shadow-lg"
                  >
                    <div className="space-y-2 flex-1">
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="font-bold text-white text-sm">{task.title}</span>
                        {task.milestone_title && (
                          <Badge variant="outline" className="text-[10px] border-teal-500/40 text-teal-300 bg-teal-500/10">
                            {task.milestone_title}
                          </Badge>
                        )}
                        <Badge
                          className={`text-[10px] uppercase font-bold ${
                            task.status === 'done' || task.status === 'completed'
                              ? 'bg-emerald-500/15 text-emerald-300 border-emerald-500/30'
                              : task.status === 'in_review'
                              ? 'bg-amber-500/15 text-amber-300 border-amber-500/30'
                              : task.status === 'in_progress'
                              ? 'bg-teal-500/15 text-teal-300 border-teal-500/30'
                              : 'bg-slate-800 text-slate-400 border-slate-700'
                          }`}
                        >
                          {task.status === 'done' ? 'Completed' : task.status.replace('_', ' ')}
                        </Badge>
                        {task.priority && (
                          <span className={`text-[10px] font-semibold uppercase px-2 py-0.5 rounded-full ${
                            task.priority === 'urgent'
                              ? 'bg-red-500/15 text-red-300 border border-red-500/30'
                              : task.priority === 'high'
                              ? 'bg-orange-500/15 text-orange-300 border border-orange-500/30'
                              : 'bg-slate-800 text-slate-400'
                          }`}>
                            {task.priority} Priority
                          </span>
                        )}
                      </div>

                      {task.description && (
                        <p className="text-xs text-slate-400 leading-relaxed max-w-3xl">
                          {task.description}
                        </p>
                      )}

                      <div className="flex flex-wrap items-center gap-4 text-xs text-slate-500 pt-1">
                        <span>Project: <strong className="text-slate-300">{task.project_name}</strong></span>
                        {task.due_date && (
                          <span>Target Due: <strong className="text-slate-300">{task.due_date}</strong></span>
                        )}
                        {task.completed_at && (
                          <span className="text-emerald-400">
                            Completed: {new Date(task.completed_at).toLocaleDateString()}
                          </span>
                        )}
                      </div>
                    </div>

                    {/* Progress Percentage Bar */}
                    <div className="w-full md:w-48 shrink-0 space-y-1.5 self-center md:self-auto bg-slate-950/60 p-3 rounded-xl border border-slate-800">
                      <div className="flex justify-between items-center text-xs">
                        <span className="text-slate-400 text-[10px] uppercase font-bold">Execution</span>
                        <span className="font-mono font-bold text-teal-300">{task.progress_percent || 0}%</span>
                      </div>
                      <div className="h-1.5 w-full bg-slate-800 rounded-full overflow-hidden">
                        <div
                          className="h-full bg-gradient-to-r from-teal-500 to-emerald-400 transition-all duration-500 rounded-full"
                          style={{ width: `${Math.min(100, Math.max(0, task.progress_percent || 0))}%` }}
                        />
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        )}

        {/* TAB 3: PROPOSALS & SCOPE */}
        {activeTab === 'proposals' && (
          <div className="space-y-6">
            <div>
              <h2 className="text-lg font-bold text-white">Proposals & Statement of Work</h2>
              <p className="text-xs text-slate-400">Review project architecture, agreed scope, and delivery commitments</p>
            </div>

            <div className="grid grid-cols-1 gap-5">
              {filteredProposals.length === 0 ? (
                <div className="p-8 text-center bg-slate-900 border border-slate-800 rounded-2xl text-slate-500 text-xs">
                  No proposals found for selected scope.
                </div>
              ) : (
                filteredProposals.map((prop) => (
                  <Card key={prop.id} className="bg-slate-900 border border-slate-800 rounded-2xl overflow-hidden shadow-xl">
                    <CardContent className="p-6 space-y-4">
                      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 pb-4 border-b border-slate-800">
                        <div>
                          <div className="flex items-center space-x-2">
                            <h3 className="text-base font-bold text-white">{prop.title}</h3>
                            <Badge className="bg-teal-500/15 text-teal-300 border-teal-500/30 text-[10px]">
                              {prop.status === 'accepted' ? 'Accepted & In Production' : 'Pending Review'}
                            </Badge>
                          </div>
                          <p className="text-xs text-slate-400 mt-0.5">Project ID: {prop.project_id} &bull; Platform: {prop.platform}</p>
                        </div>
                        <div className="text-right">
                          <span className="text-[10px] uppercase text-slate-400 font-bold block">Agreed Budget</span>
                          <span className="font-mono text-lg font-extrabold text-white">
                            {prop.currency} ${prop.budget?.toLocaleString()}
                          </span>
                        </div>
                      </div>

                      <div className="space-y-2">
                        <h4 className="text-xs font-bold text-slate-300 uppercase tracking-wider">Scope of Work Specification</h4>
                        <div className="p-4 bg-slate-950/70 border border-slate-800/80 rounded-xl text-xs text-slate-300 leading-relaxed whitespace-pre-line">
                          {prop.scope_summary}
                        </div>
                      </div>

                      <div className="p-3 bg-slate-950/40 border border-slate-800/60 rounded-xl text-[11px] text-slate-400 leading-relaxed">
                        <strong className="text-slate-300">Terms & IP Transfer:</strong> {prop.terms}
                      </div>
                    </CardContent>
                  </Card>
                ))
              )}
            </div>
          </div>
        )}

        {/* TAB 3: MILESTONES & SIGN-OFF */}
        {activeTab === 'milestones' && (
          <div className="space-y-6">
            <div>
              <h2 className="text-lg font-bold text-white">Milestones & Digital Sign-Off</h2>
              <p className="text-xs text-slate-400">
                Inspect deliverable progress, test outcomes, and authorize milestone completion via tamper-sealed E-Signatures
              </p>
            </div>

            <div className="space-y-4">
              {filteredMilestones.length === 0 ? (
                <div className="p-8 text-center bg-slate-900 border border-slate-800 rounded-2xl text-slate-500 text-xs">
                  No milestones found.
                </div>
              ) : (
                filteredMilestones.map((ms) => {
                  const isApproved = ms.is_approved || !!ms.certificate_id;
                  const canSign = (ms.status === 'completed' || ms.status === 'delivered') && !isApproved;

                  return (
                    <Card
                      key={ms.id}
                      className={`bg-slate-900 border transition-all rounded-2xl overflow-hidden ${
                        isApproved
                          ? 'border-teal-500/40 shadow-lg'
                          : canSign
                          ? 'border-amber-500/50 shadow-xl'
                          : 'border-slate-800'
                      }`}
                    >
                      <CardContent className="p-6 space-y-4">
                        <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-3 pb-3 border-b border-slate-800">
                          <div>
                            <div className="flex items-center space-x-2">
                              <span className="w-6 h-6 rounded-full bg-slate-800 border border-slate-700 flex items-center justify-center text-xs font-mono font-bold text-teal-400">
                                {ms.order_index || 1}
                              </span>
                              <h3 className="text-base font-bold text-white">{ms.title}</h3>
                            </div>
                            <p className="text-xs text-slate-400 mt-1">{ms.project_name}</p>
                          </div>

                          <div className="flex items-center space-x-2">
                            {isApproved ? (
                              <Badge className="bg-emerald-500/15 text-emerald-300 border-emerald-500/30 text-xs py-1 px-2.5 flex items-center space-x-1">
                                <ShieldCheck className="w-3.5 h-3.5" />
                                <span>Accepted & Signed</span>
                              </Badge>
                            ) : (
                              <Badge
                                variant="outline"
                                className={`text-xs uppercase py-1 px-2.5 ${
                                  ms.status === 'completed' || ms.status === 'delivered'
                                    ? 'border-amber-500/50 text-amber-300 bg-amber-500/10'
                                    : 'border-slate-700 text-slate-400'
                                }`}
                              >
                                {ms.status.replace('_', ' ')}
                              </Badge>
                            )}
                          </div>
                        </div>

                        <div className="space-y-1">
                          <span className="text-[10px] font-bold uppercase tracking-wider text-slate-400">Deliverables & Scope</span>
                          <p className="text-xs text-slate-300 leading-relaxed whitespace-pre-line bg-slate-950/50 p-3.5 rounded-xl border border-slate-800/80">
                            {ms.description || 'Milestone scope and review items.'}
                          </p>
                        </div>

                        {/* Sign-Off Area */}
                        <div className="pt-2 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                          <div className="text-xs text-slate-400">
                            {ms.due_date && (
                              <span>
                                Target Handover: <strong className="text-slate-200">{new Date(ms.due_date).toLocaleDateString()}</strong>
                              </span>
                            )}
                            {ms.approved_at && (
                              <span className="block text-[11px] text-teal-400 mt-0.5">
                                Signed by {ms.approved_by_name} on {new Date(ms.approved_at).toLocaleDateString()} &bull; Cert {ms.certificate_id}
                              </span>
                            )}
                          </div>

                          <div className="flex items-center space-x-2">
                            {canSign && (
                              <Button
                                size="sm"
                                onClick={() => handleOpenSignModal(ms)}
                                className="bg-teal-600 hover:bg-teal-500 text-white font-bold text-xs shadow-lg shadow-teal-900/30 flex items-center space-x-1.5"
                              >
                                <PenTool className="w-3.5 h-3.5" />
                                <span>Sign & Approve Milestone</span>
                              </Button>
                            )}

                            {isApproved && ms.certificate_id && (
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => setViewingCertificate({
                                  certificate_id: ms.certificate_id!,
                                  certificate_hash: ms.certificate_hash || '',
                                  project_id: ms.project_id || '',
                                  project_name: ms.project_name || '',
                                  client_name: dashboardData.client.name,
                                  milestone_id: ms.id,
                                  milestone_title: ms.title,
                                  milestone_order: ms.order_index || 1,
                                  completed_at: ms.approved_at || '',
                                  approved_at: ms.approved_at || '',
                                  signer_name: ms.approved_by_name || 'Client',
                                  signer_title: ms.approved_by_title || 'Authorized Client Representative',
                                  signer_ip: ms.signer_ip || '127.0.0.1',
                                  signature_data: ms.approved_signature_data || '',
                                  verification_status: 'VALID_TAMPER_SEALED',
                                  issued_by: 'Techosolution Project OS Signature Service',
                                })}
                                className="border-teal-500/40 text-teal-300 hover:bg-teal-500/10 text-xs flex items-center space-x-1.5"
                              >
                                <Award className="w-3.5 h-3.5 text-teal-400" />
                                <span>View Handover Certificate</span>
                              </Button>
                            )}
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  );
                })
              )}
            </div>
          </div>
        )}

        {/* TAB 4: DELIVERABLES & FILES */}
        {activeTab === 'deliverables' && (
          <div className="space-y-6">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
              <div>
                <h2 className="text-lg font-bold text-white">Shared Deliverables & Project Files</h2>
                <p className="text-xs text-slate-400">Official Figma UX designs, technical blueprints, architecture proofs, and certificates</p>
              </div>

              {/* Filter Tabs */}
              <div className="flex items-center space-x-1 bg-slate-900 p-1 border border-slate-800 rounded-xl text-xs">
                {(['all', 'design', 'technical', 'legal'] as const).map((cat) => (
                  <button
                    key={cat}
                    type="button"
                    onClick={() => setFileFilter(cat)}
                    className={`px-3 py-1 rounded-lg uppercase text-[10px] font-bold transition-colors ${
                      fileFilter === cat ? 'bg-teal-600 text-white' : 'text-slate-400 hover:text-white'
                    }`}
                  >
                    {cat}
                  </button>
                ))}
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {filteredFiles.length === 0 ? (
                <div className="col-span-full p-8 text-center bg-slate-900 border border-slate-800 rounded-2xl text-slate-500 text-xs">
                  No files found matching the filter.
                </div>
              ) : (
                filteredFiles.map((file) => (
                  <div
                    key={file.id}
                    className="p-5 bg-slate-900 border border-slate-800 rounded-2xl flex flex-col justify-between space-y-4 hover:border-slate-700 transition-colors shadow-lg"
                  >
                    <div className="space-y-2.5">
                      <div className="flex items-start justify-between gap-2">
                        <div className="p-2.5 rounded-xl bg-slate-950 border border-slate-800 text-teal-400">
                          <FileBox className="w-5 h-5" />
                        </div>
                        <Badge variant="outline" className="text-[10px] uppercase border-slate-700 text-slate-400">
                          {file.category}
                        </Badge>
                      </div>

                      <div>
                        <h4 className="text-sm font-bold text-white leading-snug">{file.name}</h4>
                        <p className="text-[11px] text-slate-400 mt-0.5">{file.project_name}</p>
                      </div>

                      <p className="text-xs text-slate-400 leading-relaxed line-clamp-2">
                        {file.description}
                      </p>
                    </div>

                    <div className="pt-3 border-t border-slate-800/80 flex items-center justify-between text-xs text-slate-400">
                      <span>{file.size_formatted || 'Online Asset'}</span>
                      <a
                        href={file.url || '#'}
                        target={file.url?.startsWith('http') ? '_blank' : '_self'}
                        rel="noreferrer"
                        className="inline-flex items-center space-x-1 text-teal-400 hover:text-teal-300 font-semibold"
                      >
                        <span>Open Asset</span>
                        <ExternalLink className="w-3.5 h-3.5" />
                      </a>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        )}

        {/* TAB 6: INVOICES & PAYMENT TERMS */}
        {activeTab === 'invoices' && (
          <div className="space-y-6">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
              <div>
                <h2 className="text-lg font-bold text-white flex items-center space-x-2">
                  <DollarSign className="w-5 h-5 text-teal-400" />
                  <span>Payment Terms, Billing Statements &amp; Escrow Releases</span>
                </h2>
                <p className="text-xs text-slate-400">Official agency billing statements, payments history, milestone disbursement rules, and receipts</p>
              </div>

              <div className="inline-flex items-center space-x-1.5 px-3 py-1 rounded-full bg-emerald-500/10 border border-emerald-500/30 text-emerald-300 text-xs font-semibold">
                <ShieldCheck className="w-4 h-4 text-emerald-400" />
                <span>Bank-Grade 256-Bit SSL Encrypted Ledger</span>
              </div>
            </div>

            {/* Agreed Payment Terms & Escrow Schedule Card */}
            <div className="bg-gradient-to-br from-slate-900 via-slate-950 to-slate-900 border border-slate-800 rounded-2xl p-5 space-y-4 shadow-xl">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                <div className="flex items-center space-x-2.5">
                  <div className="p-2 rounded-lg bg-teal-500/10 text-teal-400">
                    <CreditCard className="w-5 h-5" />
                  </div>
                  <div>
                    <h3 className="text-sm font-bold text-white">Agreed Payment Terms &amp; Milestone Disbursement</h3>
                    <p className="text-xs text-slate-400">Transparent payment schedule Ratified with Statement of Work</p>
                  </div>
                </div>
                <Badge variant="outline" className="border-teal-500/30 text-teal-300 text-[10px] uppercase">
                  Milestone Escrow Protected
                </Badge>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-3 pt-1 text-xs">
                <div className="p-3.5 rounded-xl bg-slate-950/60 border border-slate-800/80 space-y-1.5">
                  <div className="flex items-center justify-between">
                    <span className="font-bold text-slate-200">1. Architecture Deposit</span>
                    <span className="px-1.5 py-0.5 rounded text-[9px] font-bold bg-emerald-500/10 text-emerald-400">
                      Disbursed
                    </span>
                  </div>
                  <p className="text-[11px] text-slate-400 leading-relaxed">
                    50% upfront payment upon kickoff, system blueprint, and UI/UX design ratification.
                  </p>
                </div>
                <div className="p-3.5 rounded-xl bg-slate-950/60 border border-slate-800/80 space-y-1.5">
                  <div className="flex items-center justify-between">
                    <span className="font-bold text-slate-200">2. Sprint Milestones</span>
                    <span className="px-1.5 py-0.5 rounded text-[9px] font-bold bg-teal-500/10 text-teal-400">
                      In Cadence
                    </span>
                  </div>
                  <p className="text-[11px] text-slate-400 leading-relaxed">
                    Continuous milestone reviews; deliverables tested and approved sequentially.
                  </p>
                </div>
                <div className="p-3.5 rounded-xl bg-slate-950/60 border border-slate-800/80 space-y-1.5">
                  <div className="flex items-center justify-between">
                    <span className="font-bold text-slate-200">3. Final Acceptance</span>
                    <span className="px-1.5 py-0.5 rounded text-[9px] font-bold bg-slate-800 text-slate-400">
                      Final Release
                    </span>
                  </div>
                  <p className="text-[11px] text-slate-400 leading-relaxed">
                    Remaining balance released upon final QA verification and source code handover.
                  </p>
                </div>
              </div>

              <div className="pt-2 border-t border-slate-800 flex flex-wrap items-center justify-between gap-3 text-xs text-slate-400">
                <div className="flex items-center space-x-2">
                  <span className="font-semibold text-slate-300">Accepted Payment Channels:</span>
                  <span>Bank Wire (SWIFT / IBAN) &bull; Wise &bull; Stripe Credit Card &bull; WhatsApp Payment Desk</span>
                </div>
                <div className="flex items-center space-x-1 text-emerald-400 font-semibold">
                  <ShieldCheck className="w-3.5 h-3.5" />
                  <span>256-Bit SSL Encrypted Receipts</span>
                </div>
              </div>
            </div>

            {/* Financial Summary Strip */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div className="p-4 bg-slate-900 border border-slate-800 rounded-2xl space-y-1">
                <span className="text-[10px] uppercase font-bold text-slate-400">Total Billed</span>
                <p className="font-mono text-xl font-extrabold text-white">
                  {dashboardData.financial_summary?.currency || 'USD'} ${dashboardData.financial_summary?.total_invoiced?.toLocaleString()}
                </p>
              </div>
              <div className="p-4 bg-slate-900 border border-slate-800 rounded-2xl space-y-1">
                <span className="text-[10px] uppercase font-bold text-slate-400">Total Paid</span>
                <p className="font-mono text-xl font-extrabold text-emerald-400">
                  {dashboardData.financial_summary?.currency || 'USD'} ${dashboardData.financial_summary?.total_paid?.toLocaleString()}
                </p>
              </div>
              <div className="p-4 bg-slate-900 border border-slate-800 rounded-2xl space-y-1">
                <span className="text-[10px] uppercase font-bold text-slate-400">Balance Outstanding</span>
                <p className={`font-mono text-xl font-extrabold ${
                  (dashboardData.financial_summary?.balance_due || 0) > 0 ? 'text-amber-400' : 'text-emerald-400'
                }`}>
                  {dashboardData.financial_summary?.currency || 'USD'} ${dashboardData.financial_summary?.balance_due?.toLocaleString()}
                </p>
              </div>
            </div>

            {/* Invoices List */}
            <div className="space-y-3">
              {(dashboardData.invoices || []).length === 0 ? (
                <div className="p-8 text-center bg-slate-900 border border-slate-800 rounded-2xl text-slate-500 text-xs">
                  No billing statements logged.
                </div>
              ) : (
                dashboardData.invoices.map((inv) => (
                  <div
                    key={inv.id}
                    className="p-5 bg-slate-900 border border-slate-800 rounded-2xl flex flex-col sm:flex-row sm:items-center justify-between gap-4 shadow-lg hover:border-slate-700 transition-colors"
                  >
                    <div className="space-y-1">
                      <div className="flex items-center space-x-2">
                        <span className="font-mono font-bold text-white text-sm">{inv.invoice_number}</span>
                        <Badge
                          className={`text-[10px] uppercase ${
                            inv.status === 'paid'
                              ? 'bg-emerald-500/15 text-emerald-300 border-emerald-500/30'
                              : inv.status === 'overdue'
                              ? 'bg-red-500/15 text-red-300 border-red-500/30'
                              : 'bg-amber-500/15 text-amber-300 border-amber-500/30'
                          }`}
                        >
                          {inv.status}
                        </Badge>
                      </div>
                      <p className="text-xs text-slate-400">
                        {inv.project_name || 'Project Deliverables'} &bull; Issued {inv.issue_date || 'Oct 2026'} &bull; Due {inv.due_date || 'N/A'}
                      </p>
                    </div>

                    <div className="flex items-center space-x-4 self-end sm:self-auto">
                      <div className="text-right">
                        <span className="text-[10px] uppercase text-slate-400 block">Total Amount</span>
                        <span className="font-mono text-base font-bold text-white">
                          ${Number(inv.total_amount)?.toLocaleString()}
                        </span>
                      </div>

                      {inv.whatsapp_payment_url && (
                        <a
                          href={inv.whatsapp_payment_url}
                          target="_blank"
                          rel="noreferrer"
                          className="px-3.5 py-2 bg-emerald-600 hover:bg-emerald-500 text-white rounded-xl text-xs font-bold flex items-center space-x-1.5 transition-colors shadow"
                        >
                          <MessageCircle className="w-3.5 h-3.5" />
                          <span>WhatsApp Billing</span>
                        </a>
                      )}
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        )}
      </main>

      {/* Signature Canvas Modal */}
      {activeSignMilestone && (
        <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-slate-900 border border-slate-800 rounded-3xl max-w-lg w-full p-6 space-y-5 shadow-2xl text-slate-100 relative">
            <button
              type="button"
              onClick={handleCloseSignModal}
              className="absolute top-5 right-5 text-slate-400 hover:text-white"
            >
              <X className="w-5 h-5" />
            </button>

            <div className="space-y-1">
              <div className="inline-flex items-center space-x-1 text-[11px] font-bold text-teal-400 uppercase tracking-wider">
                <PenTool className="w-3.5 h-3.5" />
                <span>Formal Milestone Approval</span>
              </div>
              <h3 className="text-lg font-bold text-white leading-tight">
                Authorize Acceptance: {activeSignMilestone.title}
              </h3>
            </div>

            {signError && (
              <div className="p-3 bg-red-950/60 border border-red-800/80 rounded-xl text-xs text-red-300 flex items-start space-x-2">
                <AlertCircle className="w-4 h-4 text-red-400 shrink-0 mt-0.5" />
                <span>{signError}</span>
              </div>
            )}

            <div className="grid grid-cols-2 gap-3 text-xs">
              <div className="space-y-1">
                <label className="text-[11px] text-slate-400 font-semibold">Signer Name</label>
                <Input
                  type="text"
                  value={signerName}
                  onChange={(e) => setSignerName(e.target.value)}
                  className="bg-slate-950 border-slate-800 text-white text-xs h-9"
                  placeholder="Full Legal Name"
                />
              </div>
              <div className="space-y-1">
                <label className="text-[11px] text-slate-400 font-semibold">Signer Title</label>
                <Input
                  type="text"
                  value={signerTitle}
                  onChange={(e) => setSignerTitle(e.target.value)}
                  className="bg-slate-950 border-slate-800 text-white text-xs h-9"
                  placeholder="e.g. Founder, Product Lead"
                />
              </div>
            </div>

            {/* Signature Draw / Type Mode Switch */}
            <div className="space-y-2">
              <div className="flex items-center justify-between text-xs">
                <span className="text-[11px] font-semibold text-slate-300">Digital Signature Pad</span>
                <div className="flex items-center space-x-2">
                  <button
                    type="button"
                    onClick={() => setSignMode('draw')}
                    className={`text-[11px] ${signMode === 'draw' ? 'text-teal-400 font-bold underline' : 'text-slate-500'}`}
                  >
                    Draw
                  </button>
                  <span className="text-slate-600">|</span>
                  <button
                    type="button"
                    onClick={() => setSignMode('type')}
                    className={`text-[11px] ${signMode === 'type' ? 'text-teal-400 font-bold underline' : 'text-slate-500'}`}
                  >
                    Type
                  </button>
                </div>
              </div>

              {signMode === 'draw' ? (
                <div className="relative border-2 border-dashed border-teal-500/40 rounded-2xl bg-slate-950 overflow-hidden">
                  <canvas
                    ref={canvasRef}
                    width={480}
                    height={150}
                    onMouseDown={startDrawing}
                    onMouseMove={draw}
                    onMouseUp={stopDrawing}
                    onMouseLeave={stopDrawing}
                    onTouchStart={startDrawing}
                    onTouchMove={draw}
                    onTouchEnd={stopDrawing}
                    className="w-full h-36 cursor-crosshair touch-none"
                  />
                  {!hasDrawn && (
                    <div className="absolute inset-0 flex items-center justify-center pointer-events-none text-slate-600 text-xs">
                      Draw signature here using mouse or finger
                    </div>
                  )}
                  <button
                    type="button"
                    onClick={clearCanvas}
                    className="absolute bottom-2 right-2 px-2 py-1 rounded bg-slate-800/80 hover:bg-slate-700 text-[10px] text-slate-400"
                  >
                    Clear
                  </button>
                </div>
              ) : (
                <div className="h-36 rounded-2xl bg-slate-950 border border-slate-800 flex items-center justify-center p-4">
                  <span className="text-2xl italic font-serif text-teal-400" style={{ fontFamily: '"Brush Script MT", "Caveat", cursive, serif' }}>
                    /s/ {signerName || 'Your Signature'}
                  </span>
                </div>
              )}
            </div>

            {/* Declaration Checkbox */}
            <label className="flex items-start space-x-2.5 text-xs text-slate-400 cursor-pointer select-none">
              <input
                type="checkbox"
                checked={declarationAgreed}
                onChange={(e) => setDeclarationAgreed(e.target.checked)}
                className="mt-0.5 rounded border-slate-700 bg-slate-950 text-teal-500 focus:ring-teal-500"
              />
              <span className="leading-snug">
                I hereby declare that I have reviewed the deliverables for this milestone, accept them as completed in accordance with specifications, and authorize the generation of an official digital handover certificate.
              </span>
            </label>

            {/* Modal Actions */}
            <div className="flex items-center justify-end space-x-2 pt-2">
              <Button
                variant="outline"
                size="sm"
                onClick={handleCloseSignModal}
                className="text-xs border-slate-800 text-slate-400 hover:bg-slate-800"
              >
                Cancel
              </Button>
              <Button
                size="sm"
                disabled={isSubmittingSignature}
                onClick={handleSubmitSignature}
                className="bg-teal-600 hover:bg-teal-500 text-white font-bold text-xs h-9 px-4 shadow-lg shadow-teal-900/30"
              >
                {isSubmittingSignature ? 'Sealing Signature...' : 'Seal & Authorize Acceptance'}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Certificate Modal */}
      {viewingCertificate && (
        <div className="fixed inset-0 z-50 bg-black/85 backdrop-blur-sm flex items-center justify-center p-4 sm:p-6 overflow-y-auto">
          <div className="w-full max-w-2xl my-auto">
            <CertificateCard
              cert={viewingCertificate}
              onClose={() => setViewingCertificate(null)}
              isStandalone={false}
              copiedCertId={copiedCertId}
              onCopyCertId={handleCopyCertId}
            />
          </div>
        </div>
      )}
    </div>
  );
}

function CertificateCard({
  cert,
  onClose,
  isStandalone,
  copiedCertId,
  onCopyCertId,
}: {
  cert: HandoverCertificate;
  onClose: () => void;
  isStandalone: boolean;
  copiedCertId: boolean;
  onCopyCertId: (id: string) => void;
}) {
  const handlePrint = () => {
    window.print();
  };

  return (
    <div className="bg-slate-900 border-2 border-teal-500/50 rounded-3xl shadow-2xl p-6 sm:p-10 space-y-6 relative overflow-hidden text-slate-100 print:bg-white print:text-black print:border-none print:shadow-none print:p-0">
      <div className="absolute top-0 right-0 -mt-10 -mr-10 w-48 h-48 rounded-full bg-teal-500/10 blur-2xl pointer-events-none print:hidden" />

      {!isStandalone && (
        <button
          type="button"
          onClick={onClose}
          className="absolute top-6 right-6 w-8 h-8 rounded-full bg-slate-800 hover:bg-slate-700 flex items-center justify-center text-slate-400 hover:text-white transition-colors print:hidden"
        >
          <X className="w-4 h-4" />
        </button>
      )}

      <div className="text-center space-y-2 pb-6 border-b border-slate-800 print:border-gray-300">
        <div className="inline-flex items-center space-x-2 px-3 py-1 rounded-full text-xs font-bold bg-teal-500/15 text-teal-300 border border-teal-500/30 print:border-gray-400 print:text-teal-800">
          <Award className="w-4 h-4 text-teal-400 print:text-teal-800" />
          <span>AUTHENTICATED DIGITAL HANDOVER CERTIFICATE</span>
        </div>
        <h2 className="text-2xl sm:text-3xl font-extrabold text-white tracking-tight uppercase print:text-gray-900">
          Certificate of Milestone Acceptance
        </h2>
        <p className="text-xs text-slate-400 print:text-gray-600">
          Issued under Techosolution Agency Work Management System &bull; Cryptographically Verified
        </p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 p-3.5 bg-slate-950/70 border border-teal-900/40 rounded-2xl text-xs print:bg-gray-50 print:border-gray-300">
        <div>
          <span className="text-[10px] uppercase font-bold text-slate-400 tracking-wider flex items-center space-x-1 print:text-gray-600">
            <Hash className="w-3 h-3 text-teal-400" />
            <span>Certificate ID</span>
          </span>
          <div className="flex items-center space-x-2 mt-0.5">
            <span className="font-mono font-bold text-teal-300 text-sm print:text-teal-800">
              {cert.certificate_id}
            </span>
            <button
              type="button"
              onClick={() => onCopyCertId(cert.certificate_id)}
              className="text-[10px] text-slate-400 hover:text-white print:hidden"
              title="Copy Certificate ID"
            >
              {copiedCertId ? <Check className="w-3 h-3 text-emerald-400" /> : <Copy className="w-3 h-3" />}
            </button>
          </div>
        </div>
        <div>
          <span className="text-[10px] uppercase font-bold text-slate-400 tracking-wider flex items-center space-x-1 print:text-gray-600">
            <ShieldCheck className="w-3 h-3 text-emerald-400" />
            <span>Verification Hash</span>
          </span>
          <p className="font-mono text-[10px] text-slate-300 truncate mt-0.5 print:text-gray-700" title={cert.certificate_hash}>
            {cert.certificate_hash}
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-xs">
        <div className="space-y-1 p-3 bg-slate-950/40 border border-slate-800/80 rounded-xl print:bg-transparent print:border-gray-200">
          <p className="text-[10px] uppercase text-slate-500 font-bold">Project</p>
          <p className="font-bold text-white text-sm print:text-gray-900">{cert.project_name}</p>
        </div>
        <div className="space-y-1 p-3 bg-slate-950/40 border border-slate-800/80 rounded-xl print:bg-transparent print:border-gray-200">
          <p className="text-[10px] uppercase text-slate-500 font-bold">Client</p>
          <p className="font-bold text-white text-sm print:text-gray-900">{cert.client_name}</p>
        </div>
        <div className="space-y-1 p-3 bg-slate-950/40 border border-slate-800/80 rounded-xl print:bg-transparent print:border-gray-200">
          <p className="text-[10px] uppercase text-slate-500 font-bold">Milestone</p>
          <p className="font-bold text-white text-sm print:text-gray-900">{cert.milestone_title}</p>
        </div>
        <div className="space-y-1 p-3 bg-slate-950/40 border border-slate-800/80 rounded-xl print:bg-transparent print:border-gray-200">
          <p className="text-[10px] uppercase text-slate-500 font-bold">Sign-off Timestamp</p>
          <p className="font-bold text-white text-sm print:text-gray-900">
            {new Date(cert.approved_at).toLocaleString('en-US', {
              month: 'short',
              day: 'numeric',
              year: 'numeric',
              hour: '2-digit',
              minute: '2-digit',
              timeZoneName: 'short',
            })}
          </p>
        </div>
      </div>

      <div className="border border-teal-900/50 bg-slate-950/80 rounded-2xl p-5 space-y-3 print:bg-gray-50 print:border-gray-300">
        <div className="flex items-center justify-between text-xs">
          <span className="text-[11px] uppercase font-bold text-slate-400 print:text-gray-600">
            Authorized Digital Signature
          </span>
          <span className="text-[10px] font-mono text-emerald-400 bg-emerald-950/60 border border-emerald-900/60 px-2 py-0.5 rounded print:text-emerald-800 print:bg-emerald-50">
            TAMPER-SEALED
          </span>
        </div>

        <div className="h-20 flex items-center justify-center bg-slate-900/50 rounded-xl border border-slate-800/80 p-2 print:bg-white print:border-gray-200">
          {cert.signature_data?.startsWith('data:image') ? (
            <img
              src={cert.signature_data}
              alt="Digital Signature"
              className="max-h-16 object-contain"
            />
          ) : (
            <div
              className="text-2xl font-serif italic text-teal-400 print:text-teal-800"
              style={{ fontFamily: '"Brush Script MT", "Caveat", cursive, serif' }}
            >
              /s/ {cert.signer_name}
            </div>
          )}
        </div>

        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-1 text-[11px] text-slate-400 print:text-gray-600">
          <div>
            <strong>Signer:</strong> {cert.signer_name} ({cert.signer_title})
          </div>
          <div className="font-mono text-[10px]">
            <strong>Captured IP:</strong> {cert.signer_ip || '127.0.0.1'}
          </div>
        </div>
      </div>

      <div className="p-3.5 bg-slate-950/40 border border-slate-800/80 rounded-xl text-[11px] text-slate-400 leading-relaxed print:text-gray-600 print:border-gray-200">
        <p>
          <strong className="text-slate-300 print:text-gray-800">Legal Evidentiary Notice:</strong> This immutable certificate serves as formal legal and technical evidence of deliverable completion, client acceptance, and handover authorization for milestone payment release on escrow and freelancing platforms (Fiverr, Upwork, Direct Escrow).
        </p>
      </div>

      <div className="flex flex-col sm:flex-row items-center justify-between gap-3 pt-2 print:hidden">
        <div className="text-[11px] text-slate-500">
          Verification Service &bull; Techosolution OS
        </div>

        <div className="flex items-center space-x-2">
          <Button
            size="sm"
            onClick={handlePrint}
            className="text-xs bg-teal-600 hover:bg-teal-500 text-white flex items-center space-x-1.5"
          >
            <Printer className="w-3.5 h-3.5" />
            <span>Print / Save PDF</span>
          </Button>

          {!isStandalone && (
            <Button
              variant="outline"
              size="sm"
              onClick={onClose}
              className="text-xs border-slate-700 text-slate-300 hover:bg-slate-800"
            >
              Close
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}

export default ClientPortalPage;
